import { useState } from "react";
import { ArrowLeft, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MatrixRainBackground from "@/components/auth/MatrixRainBackground";
import { apiClient } from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!email) {
      setErrorMessage("Veuillez saisir votre adresse email.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiClient.post("/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
      setEmailSent(!!res.data.email_sent);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMessage(detail || "Une erreur est survenue. Réessayez plus tard.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="forgot-password-page">
      <MatrixRainBackground />

      <div className="login-page-content w-full max-w-sm">
        <div className="mb-10 text-center">
          <img
            src="/logo-braux-white.png"
            alt="Braux Paysagistes"
            className="mx-auto h-20 w-auto"
          />
        </div>

        <div className="login-card w-full rounded-lg p-8">
          <Link
            to="/login"
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-white"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à la connexion
          </Link>

          <h1
            className="mb-2 font-['Playfair_Display'] text-2xl leading-tight text-white"
            data-testid="forgot-password-title"
          >
            Mot de passe oublié
          </h1>
          <p className="mb-6 text-sm text-zinc-400">
            Saisissez votre adresse email pour recevoir un lien de réinitialisation.
          </p>

          {submitted ? (
            <div data-testid="forgot-password-success">
              <div className="rounded-md border border-emerald-800/50 bg-emerald-900/30 p-4 text-sm text-emerald-300">
                {emailSent
                  ? "Un email contenant le lien de réinitialisation vient d'être envoyé. Vérifiez votre boîte de réception."
                  : "Si cette adresse est associée à un compte, un lien de réinitialisation a été généré."}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
                <Input
                  data-testid="forgot-password-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre adresse email"
                  autoComplete="email"
                  className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
                />
              </div>

              {errorMessage && (
                <p data-testid="forgot-password-error" className="text-sm text-rose-400">
                  {errorMessage}
                </p>
              )}

              <Button
                data-testid="forgot-password-submit-button"
                type="submit"
                disabled={isSubmitting}
                className="h-14 w-full rounded-sm bg-white text-base font-semibold text-black transition-transform duration-200 hover:scale-[1.01] hover:bg-zinc-200"
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer le lien"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
